import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard,
  MapPin,
  Users,
  UserMinus,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  PanelLeftClose,
  PanelLeftOpen,
  Activity,
  RefreshCw,
  Settings,
  FileText,
  MessageSquare,
  UserCircle,
  Bell,
  AlertTriangle,
  Download,
  Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import axios from 'axios';
import { API_BASE_URL as API } from '@/lib/apiBase';
import usePWAInstall from '@/hooks/usePWAInstall';

const AppLayout = () => {
  const { user: currentUser, logout, isSuperAdmin, isStaff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pwa = usePWAInstall();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      // Default to collapsed for more content real estate. If the user has
      // explicitly toggled before, respect their saved preference.
      const v = localStorage.getItem('sidebar_collapsed');
      if (v === '1') return true;
      if (v === '0') return false;
      return true; // first-visit default
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('sidebar_collapsed', sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  // Notifications: poll lightweight counts every 60s
  const [notifCounts, setNotifCounts] = useState({ sla_breaches: 0, my_open_tickets: 0, total: 0 });
  useEffect(() => {
    let mounted = true;
    const fetchNotifs = async () => {
      try {
        const { data } = await axios.get(`${API}/tickets/notifications`);
        if (mounted && data) setNotifCounts(data);
      } catch {
        /* ignore */
      }
    };
    fetchNotifs();
    const timer = setInterval(fetchNotifs, 60000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/tickets?view=dashboard', icon: LayoutDashboard, label: 'Dashboard', matchPath: '/tickets', matchSearch: 'view=dashboard' },
    { to: '/tickets?view=list', icon: MessageSquare, label: 'Support Tickets', matchPath: '/tickets', matchSearch: 'view=list' },
  ];

  // Request/Support items (available for all authenticated users)
  // Note: Sync Requests and Offboarding are routable but hidden from the nav
  // (still accessible by direct URL / notification links) per product spec.
  const requestItems = [];


  const adminItems = [
    { to: '/locations', icon: MapPin, label: 'Locations' },
    { to: '/activity', icon: Activity, label: 'Activity' },
    { to: '/users', icon: Users, label: 'Users' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  const getNavItemClass = ({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`;

  // For nav entries that share a path but differ by query (e.g. /tickets?view=dashboard vs list),
  // derive the active state from the query string ourselves.
  const getQueryAwareNavClass = (item) => () => {
    const activePath = location.pathname === item.matchPath;
    const params = new URLSearchParams(location.search);
    const currentView = params.get('view');
    let active = false;
    if (activePath) {
      if (item.matchSearch === 'view=dashboard') active = currentView !== 'list' && currentView !== 'kanban';
      else if (item.matchSearch === 'view=list') active = currentView === 'list' || currentView === 'kanban';
    }
    return `sidebar-link ${active ? 'active' : ''}`;
  };

  return (
    <div className="app-container bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static lg:relative inset-y-0 left-0 z-50
        ${sidebarCollapsed ? 'w-16' : 'w-64'} sidebar flex flex-col
        transform transition-[transform,width] duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-3 border-b border-slate-200">
          <div className={`flex items-center gap-2 ${sidebarCollapsed ? 'justify-center w-full' : ''}`}>
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">GS</span>
            </div>
            {!sidebarCollapsed && (
              <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                CRM Sync
              </span>
            )}
          </div>
          {!sidebarCollapsed && (
            <button
              className="lg:hidden p-1 hover:bg-slate-100 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          )}
        </div>

        {/* Desktop collapse toggle */}
        <button
          type="button"
          onClick={() => setSidebarCollapsed((v) => !v)}
          className="hidden lg:flex items-center justify-center absolute -right-3 top-20 w-6 h-6 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50 text-slate-500 z-10"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          data-testid="sidebar-collapse-toggle"
        >
          {sidebarCollapsed ? <PanelLeftOpen className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-3.5 h-3.5" />}
        </button>

        {/* Navigation */}
        <nav className={`flex-1 ${sidebarCollapsed ? 'px-2 py-4' : 'p-4'} space-y-1 overflow-y-auto`}>
          {navItems.map((item) => {
            const { to, icon: Icon, label } = item;
            return (
              <NavLink
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={item.matchSearch ? getQueryAwareNavClass(item) : getNavItemClass}
                data-testid={`nav-${label.toLowerCase()}`}
                title={sidebarCollapsed ? label : undefined}
                end
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{label}</span>}
              </NavLink>
            );
          })}

          {requestItems.length > 0 && (
            <>
              {!sidebarCollapsed && (
                <div className="pt-4 pb-2">
                  <p className="px-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Requests
                  </p>
                </div>
              )}
              {sidebarCollapsed && <div className="pt-3 pb-1 border-t border-slate-100 mx-2" />}
              {requestItems.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setSidebarOpen(false)}
                  className={getNavItemClass}
                  data-testid={`nav-${label.toLowerCase()}`}
                  title={sidebarCollapsed ? label : undefined}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>{label}</span>}
                </NavLink>
              ))}
            </>
          )}
          
          {isSuperAdmin() && (
            <>
              {!sidebarCollapsed && (
                <div className="pt-4 pb-2">
                  <p className="px-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Admin
                  </p>
                </div>
              )}
              {sidebarCollapsed && <div className="pt-3 pb-1 border-t border-slate-100 mx-2" />}
              {adminItems.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setSidebarOpen(false)}
                  className={getNavItemClass}
                  data-testid={`nav-${label.toLowerCase()}`}
                  title={sidebarCollapsed ? label : undefined}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>{label}</span>}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* User section (with dropdown for Profile / Logout) + Notification Bell */}
        <div className={`${sidebarCollapsed ? 'p-2' : 'p-3'} border-t border-slate-200`}>
          <div className={`flex items-center ${sidebarCollapsed ? 'flex-col gap-2' : 'gap-2'}`}>
            {/* Notification bell */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`relative flex-shrink-0 rounded-md hover:bg-slate-100 transition-colors p-2 ${
                    sidebarCollapsed ? '' : 'order-2'
                  }`}
                  data-testid="notifications-bell-btn"
                  title="Notifications"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  {notifCounts.total > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
                      data-testid="notifications-count-badge"
                    >
                      {notifCounts.total > 99 ? '99+' : notifCounts.total}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                side={sidebarCollapsed ? 'right' : 'top'}
                align={sidebarCollapsed ? 'start' : 'end'}
                className="w-72 p-0"
                data-testid="notifications-popover"
              >
                <div className="px-3 py-2 border-b border-slate-100">
                  <div className="text-sm font-semibold text-slate-900">Notifications</div>
                  <div className="text-xs text-slate-500">Ticket alerts assigned to you</div>
                </div>
                <div className="p-2 space-y-1">
                  <button
                    type="button"
                    onClick={() => navigate('/tickets?view=list&breached=1')}
                    className="w-full flex items-start gap-2 px-2 py-2 rounded-md hover:bg-rose-50 text-left transition-colors"
                    data-testid="notif-sla-breaches-row"
                  >
                    <AlertTriangle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">SLA breaches</div>
                      <div className="text-xs text-slate-500">Tickets past due</div>
                    </div>
                    <span className={`text-xs font-semibold ${notifCounts.sla_breaches > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                      {notifCounts.sla_breaches}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/tickets?view=list&assignee=me')}
                    className="w-full flex items-start gap-2 px-2 py-2 rounded-md hover:bg-indigo-50 text-left transition-colors"
                    data-testid="notif-my-tickets-row"
                  >
                    <MessageSquare className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">Assigned to me</div>
                      <div className="text-xs text-slate-500">Open tickets you own</div>
                    </div>
                    <span className={`text-xs font-semibold ${notifCounts.my_open_tickets > 0 ? 'text-indigo-600' : 'text-slate-400'}`}>
                      {notifCounts.my_open_tickets}
                    </span>
                  </button>
                </div>
              </PopoverContent>
            </Popover>

            {/* User menu (order-1 on expanded so it's on the left; on collapsed, bell stacks on top) */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={`flex items-center rounded-md hover:bg-slate-100 transition-colors ${
                  sidebarCollapsed ? 'justify-center p-1' : 'gap-3 p-2 flex-1 min-w-0 order-1'
                }`}
                data-testid="user-menu-btn"
                title={sidebarCollapsed ? `${currentUser?.name || ''} · menu` : undefined}
              >
                <Avatar className="w-9 h-9 flex-shrink-0">
                  <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm font-medium">
                    {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                {!sidebarCollapsed && (
                  <>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-slate-900 truncate">{currentUser?.name}</p>
                      <p className="text-xs text-slate-500 truncate">{currentUser?.email}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 rotate-90 flex-shrink-0" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side={sidebarCollapsed ? 'right' : 'top'}
              align={sidebarCollapsed ? 'start' : 'end'}
              className="w-56"
            >
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                <UserCircle className="w-4 h-4 mr-2" />
                Profile & PIN
              </DropdownMenuItem>
              {(pwa.canInstall || pwa.isIos) && !pwa.isStandalone && (
                <>
                  <DropdownMenuSeparator />
                  {pwa.canInstall ? (
                    <DropdownMenuItem
                      onClick={async (e) => {
                        e.preventDefault();
                        await pwa.install();
                      }}
                      data-testid="menu-install-app"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Install app
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      data-testid="menu-install-ios-hint"
                      className="text-slate-500"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      <div className="flex flex-col">
                        <span>Install app</span>
                        <span className="text-[10px] text-slate-400">Safari ▸ Share ▸ Add to Home Screen</span>
                      </div>
                    </DropdownMenuItem>
                  )}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-rose-600 focus:text-rose-600"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Mobile-only top bar (desktop reclaims the space) */}
        <header className="lg:hidden h-12 bg-white border-b border-slate-200 flex items-center justify-between px-4 sticky top-0 z-30">
          <button
            className="p-2 hover:bg-slate-100 rounded-md"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <span className="text-sm font-medium text-slate-600">CRM Sync</span>
          <span className="w-9" />
        </header>

        {/* Page content */}
        <main className={`flex-1 ${location.pathname.startsWith('/tickets') ? 'p-0' : 'p-4 lg:p-6'}`}>
          <div
            className={`animate-fade-in ${
              location.pathname.startsWith('/tickets') ? 'w-full h-full' : 'max-w-7xl mx-auto'
            }`}
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
